import { taskService } from '../../../services/taskService';
import { routineTaskService } from '../../../services/routineTaskService';
import { taskService as quantifiedTaskService } from '../../../services/quantifiedTaskService';
import type { Task } from '../../../types/Task';
import type { RoutineTask } from '../../../types/RoutineTask';
import type { Project } from '../../../types/Project';
import type { TaskOption } from '../types';
import { toTaskOption } from './poPrefill';

/**
 * Which modal a task should open in, plus the fully loaded record that modal
 * needs. Mirrors the dispatch AllTasksPage does in `handleEditTask`.
 */
export type TaskModalTarget =
  | { kind: 'routine'; routineTask: RoutineTask; project: Project | null }
  | { kind: 'quantified'; task: Task; project: Project | null }
  | { kind: 'generic'; task: Task; project: Project | null };

/**
 * The task modals want a Project object. A task carries one when the API
 * included it; otherwise synthesize the minimum the modals read from it.
 */
export function resolveProjectForTask(task: Pick<Task, 'project' | 'projectId'>): Project | null {
  if (task.project) return task.project as Project;
  if (task.projectId) {
    return { id: task.projectId, name: '', type: 'Routine' } as Project;
  }
  return null;
}

/**
 * `TaskOption` carries no type, so read the generic task first to learn it, then
 * load the richer record the matching modal expects.
 */
export async function loadTaskModalTarget(taskId: number): Promise<TaskModalTarget> {
  const base = await taskService.getTask(taskId);
  const project = resolveProjectForTask(base);

  if (base.type === 'Routine') {
    const routineTask = await routineTaskService.getTaskDetails(taskId);
    return { kind: 'routine', routineTask, project };
  }

  if (base.type === 'Quantified') {
    try {
      const full = await quantifiedTaskService.getTask(taskId);
      return { kind: 'quantified', task: full, project };
    } catch {
      return { kind: 'quantified', task: base, project };
    }
  }

  return { kind: 'generic', task: base, project };
}

/**
 * Normalize either modal's save payload to a picker option. The API flattens a
 * routine task's `id` to the underlying `Task.id`, so the id is the grid's
 * `taskId` in both cases.
 */
export function taskOptionFromSaved(task: Task | RoutineTask): TaskOption {
  return toTaskOption(task as Parameters<typeof toTaskOption>[0]);
}
